import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X,
  Target,
  IndianRupee,
  Users,
  Calendar,
  Phone,
  FileText,
  Save,
  UserPlus,
  Loader2,
  Check,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import {
  TargetPeriod,
  SetTargetRequest,
  ExecutiveTarget,
  PERIOD_OPTIONS
} from '@/types/targets';

// Simple Label component (inline to avoid dependency on missing ui/label)
const Label: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => (
  <label className={`block text-sm font-medium text-gray-700 ${className || ''}`}>{children}</label>
);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

interface SetTargetsModalProps {
  isOpen: boolean;
  onClose: () => void;
  period: TargetPeriod;
  onSuccess?: () => void;
}

interface TeamMemberTarget {
  userId: string;
  userName: string;
  userEmail: string;
  userRole: string;
  revenueTarget: number;
  leadsTarget: number;
  meetingsTarget: number;
  followUpsTarget: number;
  proposalsTarget: number;
  hasExistingTarget: boolean;
}

export const SetTargetsModal: React.FC<SetTargetsModalProps> = ({
  isOpen,
  onClose,
  period,
  onSuccess
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'individual' | 'bulk'>('individual');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [teamTargets, setTeamTargets] = useState<TeamMemberTarget[]>([]);

  // Individual target form state
  const [individualTarget, setIndividualTarget] = useState({
    revenueTarget: '',
    leadsTarget: '',
    meetingsTarget: '',
    followUpsTarget: '',
    proposalsTarget: '',
    notes: ''
  });

  // Fetch team members
  const { data: teamMembers, isLoading: loadingTeam } = useQuery({
    queryKey: ['team-members-for-targets'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/targets/team-overview?period=${period}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch team');
      const data = await response.json();
      return data.data?.members || [];
    },
    enabled: isOpen && ['CEO', 'GROUP_MANAGER', 'MANAGER'].includes(user?.role || '')
  });

  // Fetch existing target for selected user
  const { data: existingTarget, isLoading: loadingExisting } = useQuery({
    queryKey: ['existing-target', selectedUserId, period],
    queryFn: async () => {
      const response = await fetch(
        `${API_BASE_URL}/targets/current?userId=${selectedUserId}&period=${period}`,
        { headers: getAuthHeaders() }
      );
      if (!response.ok) return null;
      const data = await response.json();
      return data.data;
    },
    enabled: isOpen && !!selectedUserId
  });

  // Update form when existing target loads
  useEffect(() => {
    if (existingTarget && selectedUserId) {
      setIndividualTarget({
        revenueTarget: existingTarget.revenueTarget?.toString() || '',
        leadsTarget: existingTarget.leadsTarget?.toString() || '',
        meetingsTarget: existingTarget.meetingsTarget?.toString() || '',
        followUpsTarget: existingTarget.followUpsTarget?.toString() || '',
        proposalsTarget: existingTarget.proposalsTarget?.toString() || '',
        notes: existingTarget.notes || ''
      });
    }
  }, [existingTarget, selectedUserId]);

  // Initialize team targets when team members load
  useEffect(() => {
    if (teamMembers && teamMembers.length > 0) {
      const targets = teamMembers.map((member: any) => ({
        userId: member.user?.id || '',
        userName: member.user?.name || '',
        userEmail: member.user?.email || '',
        userRole: member.user?.role || '',
        revenueTarget: member.target?.revenueTarget || 0,
        leadsTarget: member.target?.leadsTarget || 0,
        meetingsTarget: member.target?.meetingsTarget || 0,
        followUpsTarget: member.target?.followUpsTarget || 0,
        proposalsTarget: member.target?.proposalsTarget || 0,
        hasExistingTarget: !!member.target
      }));
      setTeamTargets(targets);
    }
  }, [teamMembers]);

  // Save individual target mutation
  const saveIndividualMutation = useMutation({
    mutationFn: async (data: SetTargetRequest) => {
      const response = await fetch(`${API_BASE_URL}/targets`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save target');
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: 'Target Saved',
        description: 'Target has been saved successfully'
      });
      queryClient.invalidateQueries({ queryKey: ['current-target'] });
      queryClient.invalidateQueries({ queryKey: ['team-overview'] });
      queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  // Save bulk targets mutation
  const saveBulkMutation = useMutation({
    mutationFn: async (targets: SetTargetRequest[]) => {
      const response = await fetch(`${API_BASE_URL}/targets/bulk`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ targets, period })
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save targets');
      }
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: 'Targets Saved',
        description: `Successfully saved ${data.data.created} targets`
      });
      queryClient.invalidateQueries({ queryKey: ['current-target'] });
      queryClient.invalidateQueries({ queryKey: ['team-overview'] });
      queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
      onSuccess?.();
      onClose();
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  const handleIndividualSave = () => {
    if (!selectedUserId) {
      toast({
        title: 'Error',
        description: 'Please select a team member',
        variant: 'destructive'
      });
      return;
    }

    saveIndividualMutation.mutate({
      userId: selectedUserId,
      period,
      revenueTarget: parseInt(individualTarget.revenueTarget) || 0,
      leadsTarget: parseInt(individualTarget.leadsTarget) || 0,
      meetingsTarget: parseInt(individualTarget.meetingsTarget) || 0,
      followUpsTarget: parseInt(individualTarget.followUpsTarget) || 0,
      proposalsTarget: parseInt(individualTarget.proposalsTarget) || 0,
      notes: individualTarget.notes
    });
  };

  const handleBulkSave = () => {
    const targetsToSave = teamTargets
      .filter(t => t.revenueTarget > 0 || t.leadsTarget > 0)
      .map(t => ({
        userId: t.userId,
        revenueTarget: t.revenueTarget,
        leadsTarget: t.leadsTarget,
        meetingsTarget: t.meetingsTarget,
        followUpsTarget: t.followUpsTarget,
        proposalsTarget: t.proposalsTarget
      }));

    if (targetsToSave.length === 0) {
      toast({
        title: 'No Changes',
        description: 'Please set at least one target value',
        variant: 'destructive'
      });
      return;
    }

    saveBulkMutation.mutate(targetsToSave);
  };

  const updateTeamTarget = (userId: string, field: keyof TeamMemberTarget, value: number) => {
    setTeamTargets(prev =>
      prev.map(t =>
        t.userId === userId ? { ...t, [field]: value } : t
      )
    );
  };

  const applyTemplateToAll = (template: 'default' | 'aggressive' | 'conservative') => {
    const templates = {
      default: { revenue: 500000, leads: 20, meetings: 15, followUps: 50, proposals: 10 },
      aggressive: { revenue: 750000, leads: 30, meetings: 20, followUps: 75, proposals: 15 },
      conservative: { revenue: 300000, leads: 10, meetings: 10, followUps: 30, proposals: 5 }
    };

    const t = templates[template];
    setTeamTargets(prev =>
      prev.map(target => ({
        ...target,
        revenueTarget: t.revenue,
        leadsTarget: t.leads,
        meetingsTarget: t.meetings,
        followUpsTarget: t.followUps,
        proposalsTarget: t.proposals
      }))
    );
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-600 to-purple-600 text-white">
            <div className="flex items-center gap-3">
              <Target className="w-6 h-6" />
              <div>
                <h2 className="text-xl font-bold">Set Targets</h2>
                <p className="text-blue-100 text-sm">
                  {PERIOD_OPTIONS.find(p => p.value === period)?.label} Period
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'individual' | 'bulk')} className="flex flex-col h-[calc(90vh-88px)]">
            <TabsList className="grid grid-cols-2 w-full max-w-md mx-auto mt-4">
              <TabsTrigger value="individual" className="flex items-center gap-2">
                <UserPlus className="w-4 h-4" />
                Individual
              </TabsTrigger>
              <TabsTrigger value="bulk" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Bulk Set
              </TabsTrigger>
            </TabsList>

            {/* Individual Tab */}
            <TabsContent value="individual" className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {/* User Selection */}
                <div>
                  <Label className="text-sm font-medium">Select Team Member</Label>
                  <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Choose a team member..." />
                    </SelectTrigger>
                    <SelectContent>
                      {loadingTeam ? (
                        <div className="p-4 text-center text-gray-500">Loading...</div>
                      ) : (
                        teamMembers?.map((member: any) => (
                          <SelectItem key={member.user.id} value={member.user.id}>
                            <div className="flex items-center gap-2">
                              <span>{member.user.name}</span>
                              <span className="text-xs text-gray-500">({member.user.role})</span>
                              {member.target && (
                                <Check className="w-3 h-3 text-green-500" />
                              )}
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {selectedUserId && (
                  <>
                    {loadingExisting ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Revenue Target */}
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <IndianRupee className="w-4 h-4 text-green-600" />
                            Revenue Target (₹)
                          </Label>
                          <Input
                            type="number"
                            min="0"
                            step="10000"
                            value={individualTarget.revenueTarget}
                            onChange={(e) => setIndividualTarget({
                              ...individualTarget,
                              revenueTarget: e.target.value
                            })}
                            placeholder="e.g., 500000"
                          />
                        </div>

                        {/* Leads Target */}
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-blue-600" />
                            Leads Target
                          </Label>
                          <Input
                            type="number"
                            min="0"
                            value={individualTarget.leadsTarget}
                            onChange={(e) => setIndividualTarget({
                              ...individualTarget,
                              leadsTarget: e.target.value
                            })}
                            placeholder="e.g., 20"
                          />
                        </div>

                        {/* Meetings Target */}
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-purple-600" />
                            Meetings Target
                          </Label>
                          <Input
                            type="number"
                            min="0"
                            value={individualTarget.meetingsTarget}
                            onChange={(e) => setIndividualTarget({
                              ...individualTarget,
                              meetingsTarget: e.target.value
                            })}
                            placeholder="e.g., 15"
                          />
                        </div>

                        {/* Follow-ups Target */}
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-orange-600" />
                            Follow-ups Target
                          </Label>
                          <Input
                            type="number"
                            min="0"
                            value={individualTarget.followUpsTarget}
                            onChange={(e) => setIndividualTarget({
                              ...individualTarget,
                              followUpsTarget: e.target.value
                            })}
                            placeholder="e.g., 50"
                          />
                        </div>

                        {/* Proposals Target */}
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-indigo-600" />
                            Proposals Target
                          </Label>
                          <Input
                            type="number"
                            min="0"
                            value={individualTarget.proposalsTarget}
                            onChange={(e) => setIndividualTarget({
                              ...individualTarget,
                              proposalsTarget: e.target.value
                            })}
                            placeholder="e.g., 10"
                          />
                        </div>

                        {/* Notes */}
                        <div className="space-y-2 md:col-span-2">
                          <Label>Notes (Optional)</Label>
                          <textarea
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            rows={2}
                            value={individualTarget.notes}
                            onChange={(e) => setIndividualTarget({
                              ...individualTarget,
                              notes: e.target.value
                            })}
                            placeholder="Any additional context..."
                          />
                        </div>
                      </div>
                    )}

                    {/* Save Button */}
                    <div className="flex justify-end pt-4">
                      <Button
                        onClick={handleIndividualSave}
                        disabled={saveIndividualMutation.isPending}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {saveIndividualMutation.isPending ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Save Target
                          </>
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </TabsContent>

            {/* Bulk Tab */}
            <TabsContent value="bulk" className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {/* Quick Templates */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-gray-600">Quick Templates:</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => applyTemplateToAll('conservative')}
                  >
                    Conservative
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => applyTemplateToAll('default')}
                  >
                    Standard
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => applyTemplateToAll('aggressive')}
                  >
                    Aggressive
                  </Button>
                </div>

                {/* Team Members Table */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left p-3 font-medium text-gray-700">Team Member</th>
                          <th className="text-center p-3 font-medium text-gray-700">
                            <IndianRupee className="w-4 h-4 inline text-green-600" /> Revenue
                          </th>
                          <th className="text-center p-3 font-medium text-gray-700">
                            <Users className="w-4 h-4 inline text-blue-600" /> Leads
                          </th>
                          <th className="text-center p-3 font-medium text-gray-700">
                            <Calendar className="w-4 h-4 inline text-purple-600" /> Meetings
                          </th>
                          <th className="text-center p-3 font-medium text-gray-700">
                            <Phone className="w-4 h-4 inline text-orange-600" /> Follow-ups
                          </th>
                          <th className="text-center p-3 font-medium text-gray-700">
                            <FileText className="w-4 h-4 inline text-indigo-600" /> Proposals
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {loadingTeam ? (
                          <tr>
                            <td colSpan={6} className="p-8 text-center">
                              <Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-600" />
                            </td>
                          </tr>
                        ) : teamTargets.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="p-8 text-center text-gray-500">
                              <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                              No team members found
                            </td>
                          </tr>
                        ) : (
                          teamTargets.map((member) => (
                            <tr key={member.userId} className="hover:bg-gray-50">
                              <td className="p-3">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-medium">
                                    {member.userName.charAt(0)}
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-900">{member.userName}</p>
                                    <p className="text-xs text-gray-500">{member.userRole}</p>
                                  </div>
                                  {member.hasExistingTarget && (
                                    <Check className="w-4 h-4 text-green-500" />
                                  )}
                                </div>
                              </td>
                              <td className="p-2">
                                <Input
                                  type="number"
                                  min="0"
                                  step="10000"
                                  className="w-24 text-center mx-auto"
                                  value={member.revenueTarget || ''}
                                  onChange={(e) => updateTeamTarget(
                                    member.userId,
                                    'revenueTarget',
                                    parseInt(e.target.value) || 0
                                  )}
                                />
                              </td>
                              <td className="p-2">
                                <Input
                                  type="number"
                                  min="0"
                                  className="w-20 text-center mx-auto"
                                  value={member.leadsTarget || ''}
                                  onChange={(e) => updateTeamTarget(
                                    member.userId,
                                    'leadsTarget',
                                    parseInt(e.target.value) || 0
                                  )}
                                />
                              </td>
                              <td className="p-2">
                                <Input
                                  type="number"
                                  min="0"
                                  className="w-20 text-center mx-auto"
                                  value={member.meetingsTarget || ''}
                                  onChange={(e) => updateTeamTarget(
                                    member.userId,
                                    'meetingsTarget',
                                    parseInt(e.target.value) || 0
                                  )}
                                />
                              </td>
                              <td className="p-2">
                                <Input
                                  type="number"
                                  min="0"
                                  className="w-20 text-center mx-auto"
                                  value={member.followUpsTarget || ''}
                                  onChange={(e) => updateTeamTarget(
                                    member.userId,
                                    'followUpsTarget',
                                    parseInt(e.target.value) || 0
                                  )}
                                />
                              </td>
                              <td className="p-2">
                                <Input
                                  type="number"
                                  min="0"
                                  className="w-20 text-center mx-auto"
                                  value={member.proposalsTarget || ''}
                                  onChange={(e) => updateTeamTarget(
                                    member.userId,
                                    'proposalsTarget',
                                    parseInt(e.target.value) || 0
                                  )}
                                />
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Save All Button */}
                <div className="flex justify-end pt-4">
                  <Button
                    onClick={handleBulkSave}
                    disabled={saveBulkMutation.isPending}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {saveBulkMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving All...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save All Targets
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default SetTargetsModal;
